import { NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DsButtonComponent } from '@design-system/ds-button/component/ds-button.component';
import { DsTextComponent } from '@design-system/ds-text/component/ds-text.component';
import { DsIconButtonComponent } from '@design-system/ds-icon-button/component/ds-icon-button.component';
import { BaseDropdownInterface } from '@interfaces/base-dropdown.interface';
import { NotificationService } from '@notification/service';
import { LocalStorageService } from '@services/local-storage.service';
import { reportMonthYearList } from '@utils/report-time-year';
import { catchError, combineLatest, EMPTY, filter, finalize, merge, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { IBlockTimeItem } from 'src/app/ReusableComponent/block-time-report/block-time-report.model';
import { AircraftInterface } from 'src/app/ReusableComponent/block-time-report/interfaces/aircraft.interface';
import { TripBlockInfoInterfaces } from 'src/app/ReusableComponent/block-time-report/interfaces/trip-block-info.interfaces';
import { canVerifyIbRepo } from '../../ib-repo-verification/helpers/can-verify-ib-repo.helper';
import { VerifyIbRepositionPayload } from '../../ib-repo-verification/interfaces/verify-ib-reposition-payload.interface';
import { BlockTimeRecordModalModeEnum } from '../enums/block-time-record-modal-mode.enum';
import { buildBlockTimeRecordPayload } from '../helpers/build-block-time-record-payload.helper';
import { calculateActualBlockTime } from '../helpers/calculate-actual-block-time.helper';
import { createEmptyBlockTimeRecord } from '../helpers/create-empty-block-time-record.helper';
import { isIbRepoHoursExcluded } from '../helpers/is-ib-repo-hours-excluded.helper';
import { mapBlockTimeFacts } from '../helpers/map-block-time-facts.helper';
import { mapBlockTimeHoursCards } from '../helpers/map-block-time-hours-cards.helper';
import { mapIbRepoVerificationState } from '../helpers/map-ib-repo-verification-state.helper';
import { toDropdownOptions } from '../helpers/to-dropdown-options.helper';
import { toNullableNumber } from '../helpers/to-nullable-number.helper';
import { BlockTimeFactInterface } from '../interfaces/block-time-fact.interface';
import { BlockTimeHoursCardsInterface } from '../interfaces/block-time-hours-cards.interface';
import { BlockTimeRecordModalDataInterface } from '../interfaces/block-time-record-modal-data.interface';
import { BlockTimeRecordModalResultInterface } from '../interfaces/block-time-record-modal-result.interface';
import { IbRepoVerificationStateInterface } from '../interfaces/ib-repo-verification-state.interface';
import { BtrBillingInfoComponent } from '../modules/btr-billing-info/component/btr-billing-info.component';
import { BtrHoursCardComponent } from '../modules/btr-hours-card/component/btr-hours-card.component';
import { BtrIbRepoStatusComponent } from '../modules/btr-ib-repo-status/component/btr-ib-repo-status.component';
import { BtrIbRepoVerificationPanelComponent } from '../modules/btr-ib-repo-verification-panel/component/btr-ib-repo-verification-panel.component';
import { BtrSectionTitleComponent } from '../modules/btr-section-title/component/btr-section-title.component';
import { BtrServiceRequestDetailsComponent } from '../modules/btr-service-request-details/component/btr-service-request-details.component';
import { buildValidationMessage } from '../helpers/build-validation-message.helper';
import { formatBlockHours } from '../helpers/format-block-hours.helper';
import {
  BLOCK_TIME_EMPTY_VALUE,
  BLOCK_TIME_NOTE_MAX_LENGTH,
  BLOCK_TIME_VERIFICATION_SAVED_NOTE,
} from '../properties/block-time-record-modal.properties';
import { BLOCK_TIME_EXPAND_ANIMATION } from '../properties/block-time-expand-animation.properties';
import { BlockTimeRecordFormGroup } from '../types/block-time-record-form.type';
import { BlockTimeRecordModalService } from '../services/block-time-record-modal.service';

/**
 * The v2 modal that adds or edits a block time record on the Block Time Report.
 */
@Component({
  selector: 'app-block-time-record-modal',
  templateUrl: './block-time-record-modal.component.html',
  styleUrls: ['./block-time-record-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  animations: [BLOCK_TIME_EXPAND_ANIMATION],
  imports: [
    NgIf,
    ReactiveFormsModule,
    DsButtonComponent,
    DsTextComponent,
    DsIconButtonComponent,
    BtrSectionTitleComponent,
    BtrBillingInfoComponent,
    BtrHoursCardComponent,
    BtrIbRepoStatusComponent,
    BtrIbRepoVerificationPanelComponent,
    BtrServiceRequestDetailsComponent,
  ],
})
export class BlockTimeRecordModalComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly data: BlockTimeRecordModalDataInterface = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject<MatDialogRef<BlockTimeRecordModalComponent, BlockTimeRecordModalResultInterface>>(MatDialogRef);
  private readonly service = inject(BlockTimeRecordModalService);
  private readonly notificationService = inject(NotificationService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroy$ = new Subject<void>();

  @ViewChild(BtrIbRepoStatusComponent) private readonly ibRepoStatus?: BtrIbRepoStatusComponent;

  readonly isAddMode = this.data.mode === BlockTimeRecordModalModeEnum.Add;
  readonly title = this.isAddMode ? 'Add record' : 'Edit record';
  readonly saveLabel = this.isAddMode ? 'Add' : 'Save changes';
  readonly ibRepoPanelId = 'btr_ib_repo_panel';
  readonly titleColor = 'var(--tac-primary-title-color)';
  readonly verificationSavedNote = BLOCK_TIME_VERIFICATION_SAVED_NOTE;
  readonly form: BlockTimeRecordFormGroup = this.buildForm();
  readonly detailsGroup = this.isAddMode ? this.form.controls.details : null;
  readonly hoursGroup = this.isAddMode ? this.form.controls.hours : null;
  readonly isContracted = Boolean(this.data.record?.isContracted);

  draft: IBlockTimeItem = this.isAddMode ? createEmptyBlockTimeRecord() : { ...this.data.record };
  subtitle = '';
  facts: BlockTimeFactInterface[] = [];
  hoursCards: BlockTimeHoursCardsInterface | null = null;
  ibRepoState: IbRepoVerificationStateInterface | null = null;
  cancellationHours = '';
  isCancellationVisible = this.isAddMode || Boolean(this.data.record?.isCancelled);
  canVerify = false;
  isVerifyPanelOpen = false;
  /** True while the verification request runs. A close then would drop the result, and the grid would not reload. */
  isVerifyPending = false;
  isSaving = false;
  isLoadingLists = false;
  isLoadingTails = false;
  validationRun = 0;
  isIbRepoVerified = false;
  validationMessage = '';
  actualTotal = BLOCK_TIME_EMPTY_VALUE;
  estimatedTotal = BLOCK_TIME_EMPTY_VALUE;

  monthYearOptions: BaseDropdownInterface[] = [];
  tripOptions: BaseDropdownInterface[] = [];
  customerOptions: BaseDropdownInterface[] = [];
  airVendorOptions: BaseDropdownInterface[] = [];
  tailOptions: BaseDropdownInterface[] = [];
  airportOptions: BaseDropdownInterface[] = [];

  private aircraft: AircraftInterface[] = [];

  ngOnInit(): void {
    this.canVerify = !this.isAddMode && canVerifyIbRepo(this.localStorageService.getCurrentUserDetails()?.role);
    this.setMonthYearOptions(this.draft.srCreatedOnDate);
    this.refreshView();
    this.watchClose();
    this.form.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.form.valid && this.validationMessage) {
        this.validationMessage = '';
        this.cdr.markForCheck();
      }
    });

    if (this.isAddMode) {
      this.loadAddModeLists();
      this.watchAddModeChanges();
    }
  }

  ngAfterViewInit(): void {
    // shortcut: ds-icon-button has no ariaLabel input, so the modal names its inner button. Remove when the DS adds the input.
    this.host.nativeElement.querySelector('.block-time-record-modal__close button')?.setAttribute('aria-label', 'Close');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    // A close during the save or the verification hides the result, and the grid does not reload.
    if (this.isSaving || this.isVerifyPending) {
      return;
    }

    this.dialogRef.close({ isRecordSaved: false, isIbRepoVerified: this.isIbRepoVerified });
  }

  save(): void {
    if (this.isSaving || this.isVerifyPending) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.validationRun++;
      this.validationMessage = buildValidationMessage(this.form);
      this.cdr.detectChanges();
      this.focusFirstInvalidField();
      return;
    }

    const { billing, hours } = this.form.getRawValue();
    const { cost, profit } = this.form.controls.billing.controls;
    const payload = buildBlockTimeRecordPayload(this.draft, billing, this.isAddMode ? hours : null, cost.dirty || profit.dirty);

    this.isSaving = true;

    this.service
      .save(payload, !this.isAddMode)
      .pipe(
        take(1),
        finalize(() => {
          this.isSaving = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.notificationService.success(`Record ${this.isAddMode ? 'Added' : 'Edited'} Successfully!`);
          this.dialogRef.close({ isRecordSaved: true, isIbRepoVerified: this.isIbRepoVerified });
        },
        error: (error: HttpErrorResponse) => this.showErrors(error),
      });
  }

  openVerifyPanel(): void {
    this.isVerifyPanelOpen = true;
  }

  closeVerifyPanel(): void {
    if (this.isVerifyPending) {
      return;
    }

    this.isVerifyPanelOpen = false;
    this.cdr.detectChanges();
    this.ibRepoStatus?.focus();
  }

  onVerifyPendingChange(isPending: boolean): void {
    this.isVerifyPending = isPending;
    this.cdr.markForCheck();
  }

  onIbRepoVerified(payload: VerifyIbRepositionPayload): void {
    const user = this.localStorageService.getCurrentUserDetails();
    const verified: IBlockTimeItem = {
      ...this.draft,
      ibEstimatedBlockHours: payload.verifiedHours,
      ibRepoVerified: true,
      ibRepoAwaitingVerification: false,
      ibRepoVerifiedHours: payload.verifiedHours,
      ibRepoVerificationNote: payload.note,
      ibRepoVerifiedByName: user?.fullName || user?.name || null,
      ibRepoVerifiedOn: new Date().toISOString(),
    };

    this.draft = { ...verified, actualBlockTime: this.calculateTotal(verified) };
    this.isIbRepoVerified = true;
    // The panel emits the result before its request finalizes, so the pending flag is cleared here to let the panel close.
    this.isVerifyPending = false;
    this.notificationService.success('IB repo hours verified.');
    this.refreshView();
    this.closeVerifyPanel();
  }

  private buildForm(): BlockTimeRecordFormGroup {
    const record = this.isAddMode ? null : this.data.record;
    const hoursValidators = [Validators.min(0)];

    const form: BlockTimeRecordFormGroup = this.fb.group({
      details: this.fb.group({
        tripID: this.fb.control<BaseDropdownInterface | null>(null, Validators.required),
        customer: this.fb.control<BaseDropdownInterface | null>(null, Validators.required),
        airVendor: this.fb.control<BaseDropdownInterface | null>(null, Validators.required),
        tailNumber: this.fb.control<BaseDropdownInterface | null>(null, Validators.required),
      }),
      hours: this.fb.group({
        outboundReposition: this.fb.control<BaseDropdownInterface | null>(null),
        inboundReposition: this.fb.control<BaseDropdownInterface | null>(null),
        routing: this.fb.control<string | null>(null),
        obHours: this.fb.control<number | null>(null, hoursValidators),
        liveFlightHours: this.fb.control<number | null>(null, hoursValidators),
        ibHours: this.fb.control<number | null>(null, hoursValidators),
        cancellationHours: this.fb.control<number | null>(null, hoursValidators),
        estimatedBlockHours: this.fb.control<number | null>(null, hoursValidators),
      }),
      billing: this.fb.group({
        totalBilledHours: this.fb.control<number | null>(toNullableNumber(record?.totalBilledHours), hoursValidators),
        cost: this.fb.control<number | null>(record?.cost ?? null),
        profit: this.fb.control<number | null>(record?.profit ?? null),
        invoiceID: this.fb.control<string | null>(record?.invoiceID ?? null),
        monthYear: this.fb.control<BaseDropdownInterface | null>(
          record?.monthYear ? { id: record.monthYear, name: record.monthYear } : null,
          Validators.required,
        ),
        ar: this.fb.control<boolean>(Boolean(record?.ar)),
        ap: this.fb.control<boolean>(Boolean(record?.ap)),
        note: this.fb.control<string | null>(record?.note ?? null, Validators.maxLength(BLOCK_TIME_NOTE_MAX_LENGTH)),
      }),
    });

    if (!this.isAddMode) {
      // Edit mode shows the facts and the hours as text, so their controls must not block the save.
      form.controls.details.disable();
      form.controls.hours.disable();
    } else {
      form.controls.billing.controls.monthYear.disable();
    }

    return form;
  }

  private setMonthYearOptions(date: string | null): void {
    const months = date ? reportMonthYearList(date) : reportMonthYearList();
    const current = this.form.controls.billing.controls.monthYear.value?.id;

    this.monthYearOptions = toDropdownOptions(current && !months.includes(current) ? [current, ...months] : months);
  }

  private refreshView(): void {
    this.subtitle = this.draft.tripID
      ? `Trip ${this.draft.tripID} for ${this.draft.customer?.customerName ?? BLOCK_TIME_EMPTY_VALUE}`
      : 'Select a trip to fill in the service request details.';
    this.facts = mapBlockTimeFacts(this.draft);
    this.hoursCards = mapBlockTimeHoursCards(this.draft);
    this.cancellationHours = formatBlockHours(this.draft.cancellationHours);
    this.actualTotal = this.hoursCards.actual.totalHours;
    this.estimatedTotal = this.hoursCards.estimated.totalHours;
    this.ibRepoState = this.isAddMode ? null : mapIbRepoVerificationState(this.draft);
    this.cdr.markForCheck();
  }

  private calculateTotal(record: IBlockTimeItem): number {
    return calculateActualBlockTime({
      obHours: record.obEstimatedBlockHours,
      liveFlightHours: record.liveFlightHours,
      ibHours: record.ibEstimatedBlockHours,
      cancellationHours: record.cancellationHours,
      isCancelledNoCost: Boolean(record.isCancelledNoCost),
      isIbExcluded: isIbRepoHoursExcluded(record),
    });
  }

  private watchClose(): void {
    merge(this.dialogRef.backdropClick(), this.dialogRef.keydownEvents().pipe(filter((event: KeyboardEvent) => event.key === 'Escape')))
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        // The first Esc closes only the open verify panel.
        if (event instanceof KeyboardEvent && this.isVerifyPanelOpen) {
          this.closeVerifyPanel();
          return;
        }

        this.close();
      });
  }

  private focusFirstInvalidField(): void {
    const field = this.host.nativeElement.querySelector<HTMLElement>(
      '.mat-form-field-invalid input, .mat-form-field-invalid textarea, .mat-form-field-invalid [role="combobox"]',
    );

    field?.scrollIntoView({ block: 'center' });
    field?.focus();
  }

  private loadAddModeLists(): void {
    this.isLoadingLists = true;

    combineLatest([
      this.service.getTrips(),
      this.service.getCustomers(),
      this.service.getAirVendors(),
      this.service.getAirportCodes().pipe(catchError(() => of<string[]>([]))),
    ])
      .pipe(
        take(1),
        finalize(() => {
          this.isLoadingLists = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ([trips, customers, airVendors, airports]) => {
          this.tripOptions = toDropdownOptions(trips);
          this.customerOptions = customers;
          this.airVendorOptions = (airVendors ?? []).map((vendor) => ({ id: vendor.id, name: vendor.name }));
          this.airportOptions = toDropdownOptions(airports);
          this.cdr.markForCheck();
        },
        error: () => this.notificationService.error('Could not load the lists for the new record.'),
      });
  }

  private watchAddModeChanges(): void {
    const { details, hours } = this.form.controls;

    this.onSelect(details.controls.tripID.valueChanges, (trip) =>
      this.service.getTripBlockInfo(trip.id).pipe(catchError(() => this.loadError('Could not load the trip details.'))),
    ).subscribe((info: TripBlockInfoInterfaces) => this.applyTripInfo(info));

    details.controls.customer.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((customer) => {
      this.updateDraft({ customer: customer ? { customerID: customer.id, customerName: customer.name, region: null } : null });
    });

    this.onSelect(details.controls.airVendor.valueChanges, (vendor) => {
      this.aircraft = [];
      this.tailOptions = [];
      this.isLoadingTails = true;
      details.controls.tailNumber.reset();
      this.updateDraft({ airVendor: vendor.name, tailNumber: null, aircraftType: null, aircraftSize: null });

      return this.service.getAircraftForVendor(vendor.id).pipe(
        catchError(() => this.loadError('Could not load the aircraft of the vendor.')),
        finalize(() => {
          this.isLoadingTails = false;
          this.cdr.markForCheck();
        }),
      );
    }).subscribe((aircraft: AircraftInterface[]) => {
      this.aircraft = aircraft ?? [];
      this.tailOptions = toDropdownOptions(this.aircraft.map((item) => item.tailNumber));
      this.cdr.markForCheck();
    });

    details.controls.tailNumber.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((tail) => {
      const aircraft = this.aircraft.find((item) => item.tailNumber === tail?.id);

      this.updateDraft({
        tailNumber: tail?.id ?? null,
        aircraftType: aircraft?.aircraftType ?? null,
        aircraftSize: aircraft?.aircraftSize ?? null,
      });
    });

    hours.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const value = hours.getRawValue();
      const next: IBlockTimeItem = {
        ...this.draft,
        outboundReposition: value.outboundReposition?.id ?? null,
        inboundReposition: value.inboundReposition?.id ?? null,
        routing: value.routing,
        obEstimatedBlockHours: value.obHours,
        liveFlightHours: value.liveFlightHours,
        ibEstimatedBlockHours: value.ibHours,
        cancellationHours: value.cancellationHours,
        estimatedBlockHours: value.estimatedBlockHours,
      };

      this.draft = { ...next, actualBlockTime: this.calculateTotal(next) };
      this.refreshView();
    });
  }

  private onSelect<T, R>(changes: Observable<T | null>, load: (value: T) => Observable<R>): Observable<R> {
    return changes.pipe(
      filter((value): value is T => Boolean(value)),
      switchMap(load),
      takeUntil(this.destroy$),
    );
  }

  private applyTripInfo(info: TripBlockInfoInterfaces): void {
    const customer = info.customer
      ? { customerID: info.customer.customerID, customerName: info.customer.customerName, region: info.customer.region }
      : null;

    this.updateDraft({
      tripID: info.tripId || this.form.controls.details.controls.tripID.value?.id || null,
      srCreatedOnDate: info.srCreatedOnDate,
      serviceType: info.serviceType,
      additionalOrgans: info.serviceSubType,
      unosid: info.unosno,
      groundVendor: info.groundVendor,
      customer,
    });

    this.form.controls.details.controls.customer.setValue(customer ? { id: customer.customerID, name: customer.customerName } : null, {
      emitEvent: false,
    });
    this.form.controls.billing.controls.monthYear.enable();
    this.setMonthYearOptions(info.srCreatedOnDate);
    this.cdr.markForCheck();
  }

  private updateDraft(changes: Partial<IBlockTimeItem>): void {
    this.draft = { ...this.draft, ...changes };
    this.refreshView();
  }

  private loadError(message: string): Observable<never> {
    this.notificationService.error(message);

    return EMPTY;
  }

  private showErrors(error: HttpErrorResponse): void {
    const errors: string[] = error?.error?.errors ?? [];

    if (!errors.length) {
      this.notificationService.error('Something went wrong while saving the record.');
      return;
    }

    errors.forEach((message: string) => this.notificationService.error(message));
  }
}
